import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, CheckCircle, Shield, ArrowRight, 
  Brain, MessageSquare, Target, Scale, Bot, Wand2, Users,
  BarChart3, FileText, Zap, Clock, TrendingUp, Star,
  Calendar, AlertCircle, ChevronRight, Layers, Grid3x3
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRAKSISOMRAADER = [
  'Arbeidsrett',
  'Kontrakt/Corporate',
  'Familie/Arv',
  'Eiendom',
  'Tvisteløsning',
  'Straff',
  'Annet'
];

const SEGMENTER = [
  { value: 'AFA-only', label: 'AFA-only' },
  { value: 'Solo/Pro', label: 'Solo/Pro' },
  { value: 'Lite firma (2-10)', label: 'Lite firma (2-10)' },
  { value: 'Større firma (10+)', label: 'Større firma (10+)' }
];

interface FormData {
  name: string;
  email: string;
  praksisomraader: string[];
  segment: string;
}

const EarlyAccessWaitlistPremium: React.FC = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  // Simplified form state
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    praksisomraader: [],
    segment: ''
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [position, setPosition] = useState<number | null>(null);
  const [activeFeatureTab, setActiveFeatureTab] = useState('dashboard');
  
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);
  
  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };
  
  const handlePraksisomraadeToggle = (omraade: string) => {
    setFormData(prev => ({
      ...prev,
      praksisomraader: prev.praksisomraader.includes(omraade)
        ? prev.praksisomraader.filter(o => o !== omraade)
        : [...prev.praksisomraader, omraade]
    }));
    
    if (errors.praksisomraader) {
      setErrors(prev => ({ ...prev, praksisomraader: '' }));
    }
  };
  
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) newErrors.name = 'Navn er påkrevd';
    if (!formData.email.trim()) {
      newErrors.email = 'E-post er påkrevd';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Ugyldig e-postadresse';
    }
    if (formData.praksisomraader.length === 0) {
      newErrors.praksisomraader = 'Velg minst ett område';
    }
    if (!formData.segment) newErrors.segment = 'Velg segment';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Vennligst fyll ut alle felt');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      const response = await axios.post(`${API}/waitlist/signup`, {
        name: formData.name,
        email: formData.email,
        praksisomraader: formData.praksisomraader,
        segment: formData.segment,
        test_interesse: null,
        referrer: document.referrer || 'direct'
      });
      
      setIsSuccess(true);
      setPosition(response.data.position);
      
      if (response.data.already_signed_up) {
        toast.info('Du er allerede på listen!');
      } else {
        toast.success('Velkommen! Du er nå på listen.');
      }
      
    } catch (error) {
      console.error('Error submitting waitlist:', error);
      toast.error('Kunne ikke registrere. Prøv igjen.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-950 relative overflow-hidden">
        {/* Background effects */}
        <div className="fixed inset-0 -z-10">
          <div 
            className="absolute top-0 right-0 w-[800px] h-[800px] rounded-full bg-slate-500/10 blur-3xl"
            style={{
              transform: `translate(${mousePosition.x * 0.02}px, ${mousePosition.y * 0.02}px)`
            }}
          />
          <div 
            className="absolute bottom-0 left-0 w-[700px] h-[700px] rounded-full bg-purple-500/10 blur-3xl"
            style={{
              transform: `translate(${mousePosition.x * -0.015}px, ${mousePosition.y * -0.015}px)`
            }}
          />
        </div>
        
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-2xl w-full p-12 rounded-3xl bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 shadow-2xl text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
            className="w-24 h-24 bg-gradient-to-br from-emerald-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-8 shadow-xl"
          >
            <CheckCircle className="w-12 h-12 text-white" />
          </motion.div>
          
          <h1 className="text-4xl font-bold mb-4 text-white">
            Du er på listen!
          </h1>
          
          <p className="text-lg mb-8 text-slate-300">
            Takk for interessen. Vi inviterer puljevis og tar kontakt når det er din tur.
          </p>
          
          {position && (
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full mb-8 bg-slate-500/20 border border-slate-500/30">
              <Users className="w-5 h-5 text-slate-500" />
              <span className="font-semibold text-indigo-300">
                Du er #{position} på listen
              </span>
            </div>
          )}
          
          <div className="p-6 rounded-2xl bg-slate-800/50 space-y-4 border border-slate-700/50">
            <h3 className="font-bold text-white">
              Hva skjer videre?
            </h3>
            <div className="text-sm space-y-2 text-slate-400">
              <p>✓ Vi sender invitasjon til din e-post</p>
              <p>✓ Mulighet for kort pilotsamtale</p>
              <p>✓ Early access til Lexy</p>
            </div>
          </div>
          
          <p className="text-sm mt-8 text-slate-500">
            Svar gjerne på e-posten hvis du vil ta en kort pilotsamtale.
          </p>
        </motion.div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-950 relative overflow-hidden">
      
      {/* Premium dark background effects */}
      <div className="fixed inset-0 -z-10">
        <div 
          className="absolute top-0 right-0 w-[1000px] h-[1000px] rounded-full bg-slate-500/10 blur-3xl"
          style={{
            transform: `translate(${mousePosition.x * 0.02}px, ${mousePosition.y * 0.02}px)`
          }}
        />
        <div 
          className="absolute bottom-0 left-0 w-[800px] h-[800px] rounded-full bg-purple-500/10 blur-3xl"
          style={{
            transform: `translate(${mousePosition.x * -0.015}px, ${mousePosition.y * -0.015}px)`
          }}
        />
        <div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-cyan-500/5 blur-3xl"
          style={{
            transform: `translate(calc(-50% + ${mousePosition.x * 0.01}px), calc(-50% + ${mousePosition.y * 0.01}px))`
          }}
        />
        
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff03_1px,transparent_1px),linear-gradient(to_bottom,#ffffff03_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
      </div>
      
      {/* Header */}
      <motion.header 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/50"
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <svg width="44" height="44" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="200" height="200" rx="40" fill="white"/>
                <path d="M50 150V50H70V130H130V150H50Z" fill="#0F172A"/>
                <circle cx="155" cy="145" r="15" fill="#6366F1"/>
              </svg>
              <div>
                <span className="text-lg font-bold text-white">Lexy</span>
                <div className="text-xs text-slate-400 font-medium">AI-first advokatplattform</div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <Shield className="w-4 h-4 text-emerald-500" />
              <span>Early Access</span>
            </div>
          </div>
        </div>
      </motion.header>
      
      <div className="max-w-7xl mx-auto px-6 pt-32 pb-20">
        
        {/* Hero Section with Form */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-32">
          
          {/* Left: Content */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-500/10 border border-slate-500/20 mb-8"
            >
              <Sparkles className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-semibold text-indigo-300">
                AI-first advokatplattform
              </span>
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-6xl sm:text-7xl font-bold mb-6 leading-tight"
            >
              <span className="text-white">
                AI først.
              </span>
              <br />
              <span className="bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                Du bestemmer.
              </span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-xl text-slate-400 mb-10 leading-relaxed"
            >
              Lexy lager førsteutkast, prioriterer sakene dine og varsler risiko.
              <br className="hidden sm:block" />
              <span className="text-white">Du godkjenner før noe sendes til klient.</span>
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="flex flex-wrap gap-3 mb-12"
            >
              {[
                { icon: Brain, text: 'AI-drevet beslutningsstøtte' },
                { icon: Wand2, text: 'AI forbereder, du godkjenner' },
                { icon: Shield, text: 'Norsk advokatetikk' }
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-800/50 border border-slate-700/50 backdrop-blur-sm">
                  <item.icon className="w-4 h-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-300">{item.text}</span>
                </div>
              ))}
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="p-6 rounded-2xl bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-slate-700/50 backdrop-blur-sm"
            >
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">Early access-gruppen får</h3>
                  <p className="text-sm text-slate-400">Begrenset antall plasser i første cohort</p>
                </div>
              </div>
              <div className="space-y-2 text-sm text-slate-400">
                <p className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  Tidlig tilgang til produktet
                </p>
                <p className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  Direkte linje til teamet
                </p>
                <p className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  Påvirkning på AI-logikk og arbeidsflyter
                </p>
              </div>
            </motion.div>
          </motion.div>
          
          {/* Right: Simplified Form */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:sticky lg:top-32"
          >
            <div className="p-8 rounded-3xl bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 shadow-2xl">
              <h3 className="text-2xl font-bold text-white mb-2">
                Bli med på venteliste
              </h3>
              <p className="text-sm text-slate-400 mb-8">
                Registrer deg nå – vi inviterer puljevis
              </p>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Navn <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Ola Nordmann"
                    autoFocus
                    className={`w-full px-4 py-3 rounded-xl bg-slate-800/50 border focus:outline-none focus:ring-2 focus:ring-slate-500 transition-all text-white placeholder-slate-500 ${
                      errors.name ? 'border-red-500' : 'border-slate-700'
                    }`}
                  />
                  {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
                </div>
                
                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    E-post <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="ola@advokatfirma.no"
                    className={`w-full px-4 py-3 rounded-xl bg-slate-800/50 border focus:outline-none focus:ring-2 focus:ring-slate-500 transition-all text-white placeholder-slate-500 ${
                      errors.email ? 'border-red-500' : 'border-slate-700'
                    }`}
                  />
                  {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                </div>
                
                {/* Praksisområder - Simplified */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Praksisområde <span className="text-red-400">*</span>
                  </label>
                  <p className="text-xs text-slate-500 mb-3">Velg alle som passer</p>
                  <div className="grid grid-cols-2 gap-2">
                    {PRAKSISOMRAADER.map((omraade) => (
                      <button
                        key={omraade}
                        type="button"
                        onClick={() => handlePraksisomraadeToggle(omraade)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all relative ${
                          formData.praksisomraader.includes(omraade)
                            ? 'bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-lg shadow-indigo-500/50 border border-slate-400'
                            : 'bg-slate-800/50 border border-slate-700 text-slate-300 hover:bg-slate-800 hover:border-slate-600'
                        }`}
                      >
                        {formData.praksisomraader.includes(omraade) && (
                          <CheckCircle className="w-4 h-4 absolute top-1 right-1 text-white" />
                        )}
                        {omraade}
                      </button>
                    ))}
                  </div>
                  {errors.praksisomraader && <p className="text-red-400 text-xs mt-2">{errors.praksisomraader}</p>}
                </div>
                
                {/* Segment - Improved Design */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Segment <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <select
                      value={formData.segment}
                      onChange={(e) => handleInputChange('segment', e.target.value)}
                      className={`w-full px-4 py-3 rounded-xl bg-slate-800/50 border focus:outline-none focus:ring-2 focus:ring-slate-500 transition-all text-white appearance-none cursor-pointer ${
                        errors.segment ? 'border-red-500' : 'border-slate-700'
                      } ${formData.segment ? 'text-white' : 'text-slate-500'}`}
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23a855f7'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 0.75rem center',
                        backgroundSize: '1.25rem'
                      }}
                    >
                      <option value="" className="bg-slate-900 text-slate-500">Velg segment</option>
                      {SEGMENTER.map((seg) => (
                        <option key={seg.value} value={seg.value} className="bg-slate-900 text-white py-2">{seg.label}</option>
                      ))}
                    </select>
                  </div>
                  {errors.segment && <p className="text-red-400 text-xs mt-1">{errors.segment}</p>}
                </div>
                
                {/* Submit */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-violet-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-semibold py-4 rounded-xl text-lg shadow-lg hover:shadow-xl transition-all"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Registrerer...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Bli med på venteliste
                      <ArrowRight className="w-5 h-5" />
                    </span>
                  )}
                </Button>
                
                <p className="text-xs text-center text-slate-500">
                  Vi bruker din informasjon kun til early access-invitasjoner.
                </p>
              </form>
            </div>
          </motion.div>
        </div>
        
        {/* What is Lexy - Improved Section */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mb-32"
        >
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Hva er Lexy?
            </h2>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto">
              En komplett advokatplattform som kombinerer AI-drevet automatisering med full advokatkontroll. 
              <br className="hidden sm:block" />
              Fra første henvendelse til ferdig leveranse – alt på ett sted.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              {
                icon: Layers,
                title: 'Komplett Plattform',
                description: 'Alt du trenger for å drive advokatvirksomhet: saksbehandling, dokumenter, kommunikasjon, fakturering og rapportering.',
                gradient: 'from-blue-500 to-cyan-600'
              },
              {
                icon: Brain,
                title: 'AI som Medarbeider',
                description: 'AI leser, analyserer, prioriterer og lager førsteutkast. Du kvalitetssikrer og leverer. Som å ha en junior som aldri slutter.',
                gradient: 'from-violet-500 to-purple-600'
              },
              {
                icon: Shield,
                title: 'Bygget for Advokater',
                description: 'Taushetsplikt by design. GDPR-compliant. Følger Advokatforeningens AI-veiledning. Norsk datalagring.',
                gradient: 'from-emerald-500 to-green-600'
              },
              {
                icon: TrendingUp,
                title: 'Skaler Virksomheten',
                description: 'Fra én advokat til stort team. Håndter flere saker uten å ansette mer. Verdibasert prising i stedet for timer.',
                gradient: 'from-purple-500 to-pink-600'
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 + index * 0.1 }}
                className="group p-6 rounded-2xl bg-slate-900/50 backdrop-blur-sm border border-slate-700/50 hover:border-slate-600 hover:shadow-xl transition-all duration-300"
              >
                <div className={`w-12 h-12 bg-gradient-to-br ${feature.gradient} rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform shadow-lg`}>
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
        
        {/* Feature Screenshots with 5 Premium Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
          className="mb-32"
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              Se Lexy i aksjon
            </h2>
            <p className="text-slate-400">
              Premium dark workspace – designet for fokus og effektivitet
            </p>
          </div>
          
          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-8 justify-center">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: Grid3x3 },
              { id: 'intake', label: 'AI Intake', icon: MessageSquare },
              { id: 'draft', label: 'AI-utkast', icon: Wand2 },
              { id: 'workspace', label: 'Workspace', icon: FileText },
              { id: 'team', label: 'Team & Analytics', icon: Users }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveFeatureTab(tab.id)}
                className={`px-5 py-3 rounded-xl font-medium transition-all flex items-center gap-2 ${
                  activeFeatureTab === tab.id
                    ? 'bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-lg shadow-indigo-500/50 border border-slate-400'
                    : 'bg-slate-800/50 border border-slate-700 text-slate-300 hover:bg-slate-800 hover:border-slate-600'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
          
          {/* Mockup Display - Premium Components */}
          <div className="relative rounded-3xl overflow-hidden border border-slate-700/50 bg-slate-900/30 p-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeFeatureTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="min-h-[600px]"
              >
                {activeFeatureTab === 'dashboard' && (
                  <div className="space-y-6">
                    {/* Stats Row */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      {[
                        { label: 'Aktive saker', value: '24', change: '+3', icon: FileText, color: 'indigo' },
                        { label: 'Venter godkjenning', value: '8', change: '+5', icon: AlertCircle, color: 'orange' },
                        { label: 'Fullført denne uken', value: '12', change: '+4', icon: CheckCircle, color: 'emerald' },
                        { label: 'Team kapasitet', value: '87%', change: '+12%', icon: BarChart3, color: 'purple' }
                      ].map((stat, i) => (
                        <div key={i} className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
                          <div className="flex items-center justify-between mb-2">
                            <stat.icon className={`w-5 h-5 text-${stat.color}-400`} />
                            <span className="text-xs text-emerald-400">{stat.change}</span>
                          </div>
                          <div className="text-2xl font-bold text-white mb-1">{stat.value}</div>
                          <div className="text-xs text-slate-400">{stat.label}</div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Priority Cases */}
                    <div className="p-6 rounded-2xl bg-slate-800/30 border border-slate-700/50">
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-bold text-white flex items-center gap-2">
                          <Zap className="w-5 h-5 text-yellow-400" />
                          Prioriterte saker
                        </h3>
                        <button type="button" className="text-sm text-slate-500 hover:text-indigo-300">Se alle →</button>
                      </div>
                      
                      <div className="space-y-3">
                        {[
                          { title: 'Oppsigelsessak - Hansen AS', status: 'AI-utkast klart', priority: 'Høy', color: 'red' },
                          { title: 'Kontraktsgjennomgang', status: 'Under behandling', priority: 'Middels', color: 'yellow' },
                          { title: 'Inkassovarsel', status: 'Ny henvendelse', priority: 'Høy', color: 'red' }
                        ].map((case_, i) => (
                          <div key={i} className="flex items-center justify-between p-4 rounded-xl bg-slate-800/50 border border-slate-700/50 hover:border-slate-600 transition-colors cursor-pointer group">
                            <div className="flex items-center gap-4 flex-1">
                              <div className={`w-2 h-2 rounded-full bg-${case_.color}-500`}></div>
                              <div className="flex-1">
                                <div className="font-medium text-white group-hover:text-indigo-300 transition-colors">{case_.title}</div>
                                <div className="text-xs text-slate-400">{case_.status}</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                case_.priority === 'Høy' 
                                  ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                                  : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                              }`}>
                                {case_.priority}
                              </span>
                              <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-slate-300" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                
                {activeFeatureTab === 'intake' && (
                  <div className="space-y-6">
                    <div className="p-6 rounded-2xl bg-slate-800/30 border border-slate-700/50">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center">
                          <MessageSquare className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-white">Ny henvendelse fra klient</h3>
                          <p className="text-sm text-slate-400">Mottatt for 2 minutter siden</p>
                        </div>
                      </div>
                      
                      <div className="p-4 rounded-xl bg-slate-900/50 border border-slate-700/50 mb-4">
                        <p className="text-slate-300 text-sm leading-relaxed">
                          "Jeg har mottatt et oppsigelsesvarsel fra arbeidsgiver. De hevder at jeg har brutt arbeidsavtalen ved å starte et sidepro..."
                        </p>
                      </div>
                      
                      <div className="space-y-4">
                        <div className="flex items-start gap-3">
                          <Brain className="w-5 h-5 text-slate-500 mt-1" />
                          <div className="flex-1">
                            <h4 className="font-medium text-white mb-2">AI-analyse</h4>
                            <div className="space-y-2">
                              {[
                                { label: 'Identifisert område', value: 'Arbeidsrett', color: 'indigo' },
                                { label: 'Kompleksitet', value: 'Middels', color: 'yellow' },
                                { label: 'Estimert tidsbruk', value: '2-4 timer', color: 'slate' },
                                { label: 'Anbefalt handling', value: 'Full saksgjennomgang', color: 'emerald' }
                              ].map((item, i) => (
                                <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-slate-700/50">
                                  <span className="text-sm text-slate-400">{item.label}</span>
                                  <span className={`text-sm font-medium text-${item.color}-400`}>{item.value}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {activeFeatureTab === 'draft' && (
                  <div className="space-y-6">
                    <div className="p-6 rounded-2xl bg-slate-800/30 border border-slate-700/50">
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
                            <Wand2 className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-white">AI-generert juridisk vurdering</h3>
                            <p className="text-sm text-slate-400">Klar til gjennomgang</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button type="button" className="px-4 py-2 rounded-lg bg-slate-700/50 border border-slate-600 text-slate-300 text-sm hover:bg-slate-700 transition-colors">
                            Regenerer
                          </button>
                          <button type="button" className="px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-green-600 text-white text-sm font-medium hover:from-emerald-600 hover:to-green-700 transition-colors">
                            Godkjenn & Send
                          </button>
                        </div>
                      </div>
                      
                      <div className="p-5 rounded-xl bg-slate-900/50 border border-slate-700/50 space-y-4">
                        <div>
                          <h4 className="text-sm font-semibold text-slate-500 mb-2">Juridisk vurdering:</h4>
                          <p className="text-slate-300 text-sm leading-relaxed">
                            Med bakgrunn i arbeidsmiljøloven § 15-7 og praksis fra Høyesterett (Rt. 2011 s. 910), 
                            kan oppsigelsen være ugyldig dersom konkurranse-klausulen i arbeidsavtalen er for vid...
                          </p>
                        </div>
                        
                        <div className="h-px bg-slate-700/50"></div>
                        
                        <div>
                          <h4 className="text-sm font-semibold text-emerald-400 mb-2">Anbefaling:</h4>
                          <p className="text-slate-300 text-sm leading-relaxed">
                            Jeg anbefaler at du bestrider oppsigelsen skriftlig innen fristen. Vi bør også...
                          </p>
                        </div>
                        
                        <div className="flex items-center gap-2 pt-3">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          <span className="text-xs text-slate-400">Kvalitetssikret mot norsk rett og rettspraksis</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {activeFeatureTab === 'workspace' && (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left sidebar */}
                    <div className="space-y-4">
                      <div className="p-4 rounded-xl bg-slate-800/30 border border-slate-700/50">
                        <h4 className="text-sm font-semibold text-white mb-4">Saksdetaljer</h4>
                        <div className="space-y-3">
                          {[
                            { label: 'Klient', value: 'Hansen AS' },
                            { label: 'Type', value: 'Arbeidsrett' },
                            { label: 'Status', value: 'Under behandling' },
                            { label: 'Frist', value: '3 dager' }
                          ].map((detail, i) => (
                            <div key={i}>
                              <div className="text-xs text-slate-500 mb-1">{detail.label}</div>
                              <div className="text-sm text-white font-medium">{detail.value}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div className="p-4 rounded-xl bg-slate-800/30 border border-slate-700/50">
                        <h4 className="text-sm font-semibold text-white mb-4">Dokumenter</h4>
                        <div className="space-y-2">
                          {['Arbeidsavtale.pdf', 'Oppsigelse.pdf', 'Korrespondanse.docx'].map((doc, i) => (
                            <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-slate-900/50 border border-slate-700/50 hover:border-slate-600 transition-colors cursor-pointer">
                              <FileText className="w-4 h-4 text-slate-400" />
                              <span className="text-xs text-slate-300 flex-1">{doc}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    {/* Main content */}
                    <div className="lg:col-span-2 space-y-4">
                      <div className="p-6 rounded-xl bg-slate-800/30 border border-slate-700/50">
                        <h4 className="text-lg font-bold text-white mb-4">Tidslinje</h4>
                        <div className="space-y-4">
                          {[
                            { time: '10:45', action: 'AI-utkast generert', type: 'ai' },
                            { time: '09:30', action: 'Dokumenter lastet opp', type: 'doc' },
                            { time: '09:15', action: 'Sak opprettet', type: 'new' }
                          ].map((event, i) => (
                            <div key={i} className="flex gap-3">
                              <div className="w-8 h-8 rounded-full bg-slate-500/20 border border-slate-500/30 flex items-center justify-center flex-shrink-0">
                                <div className="w-2 h-2 rounded-full bg-indigo-400"></div>
                              </div>
                              <div className="flex-1 pt-1">
                                <div className="text-sm text-white font-medium">{event.action}</div>
                                <div className="text-xs text-slate-400">{event.time}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {activeFeatureTab === 'team' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Team overview */}
                      <div className="p-6 rounded-xl bg-slate-800/30 border border-slate-700/50">
                        <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                          <Users className="w-5 h-5 text-slate-500" />
                          Team oversikt
                        </h4>
                        <div className="space-y-3">
                          {[
                            { name: 'Advokat Hansen', cases: 12, status: 'online' },
                            { name: 'Advokat Berg', cases: 8, status: 'online' },
                            { name: 'Advokat Olsen', cases: 15, status: 'away' }
                          ].map((member, i) => (
                            <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-slate-900/50 border border-slate-700/50">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold">
                                  {member.name.charAt(8)}
                                </div>
                                <div>
                                  <div className="text-sm text-white font-medium">{member.name}</div>
                                  <div className="text-xs text-slate-400">{member.cases} aktive saker</div>
                                </div>
                              </div>
                              <div className={`w-2 h-2 rounded-full ${
                                member.status === 'online' ? 'bg-emerald-500' : 'bg-yellow-500'
                              }`}></div>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Analytics */}
                      <div className="p-6 rounded-xl bg-slate-800/30 border border-slate-700/50">
                        <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                          <BarChart3 className="w-5 h-5 text-purple-400" />
                          Denne måneden
                        </h4>
                        <div className="space-y-4">
                          {[
                            { label: 'Fullførte saker', value: 47, max: 60, color: 'emerald' },
                            { label: 'AI-timer spart', value: 156, max: 200, color: 'indigo' },
                            { label: 'Klienttilfredshet', value: 94, max: 100, color: 'purple' }
                          ].map((stat, i) => (
                            <div key={i}>
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm text-slate-400">{stat.label}</span>
                                <span className={`text-sm font-bold text-${stat.color}-400`}>{stat.value}</span>
                              </div>
                              <div className="h-2 rounded-full bg-slate-900/50 overflow-hidden">
                                <div 
                                  className={`h-full bg-gradient-to-r from-${stat.color}-500 to-${stat.color}-600`}
                                  style={{ width: `${(stat.value / stat.max) * 100}%` }}
                                ></div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
        
        {/* AI-first in practice - Dark Box */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4 }}
        >
          <div className="p-12 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 border border-slate-700/50 relative overflow-hidden shadow-2xl">
            <div className="absolute top-0 right-0 w-96 h-96 bg-slate-500/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"></div>
            
            <div className="relative">
              <div className="flex items-center gap-3 mb-10">
                <Bot className="w-10 h-10 text-slate-500" />
                <h2 className="text-3xl font-bold text-white">AI-first i praksis</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {[
                  {
                    step: '01',
                    title: 'Klient sender henvendelse',
                    description: 'AI leser, forstår problemstillingen, og identifiserer relevante juridiske spørsmål automatisk.',
                    icon: MessageSquare
                  },
                  {
                    step: '02',
                    title: 'AI prioriterer og vurderer',
                    description: 'Kompleksitet, risiko og hastighet vurderes. Saken kategoriseres og plasseres i din arbeidsliste.',
                    icon: Target
                  },
                  {
                    step: '03',
                    title: 'AI genererer førsteutkast',
                    description: 'Juridisk svar, anbefalinger og neste steg skrives av AI basert på saksdetaljer og gjeldende rett.',
                    icon: Wand2
                  },
                  {
                    step: '04',
                    title: 'Du godkjenner og leverer',
                    description: 'Du bruker tid på kvalitetssikring og advokathåndverk, ikke på rutinearbeid. AI har gjort grunnarbeidet.',
                    icon: CheckCircle
                  }
                ].map((step, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-xl bg-slate-800/50 backdrop-blur-sm flex items-center justify-center border border-slate-700/50">
                        <step.icon className="w-6 h-6 text-slate-500" />
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-bold text-slate-500 mb-1">
                        {step.step}
                      </div>
                      <h4 className="font-bold text-lg text-white mb-2">{step.title}</h4>
                      <p className="text-slate-400 text-sm leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default EarlyAccessWaitlistPremium;